import { ArrowLeft, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router'

import { cx } from '../lib/cx'
import { useI18n, type Lang } from '../lib/i18n'
import { Container } from './ui'

export const BRAND = 'URG ISPANZA EXPORT LLC'

const links = [
  { to: '/shop', key: 'shop' },
  { to: '/#kategoriyalar', key: 'categories' },
  { to: '/about', key: 'about' },
  { to: '/#aloqa', key: 'contact' },
]

const languages: { code: Lang; label: string }[] = [
  { code: 'uz', label: 'O‘zbek' },
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
]

export function BrandMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3.5 11.5h17a8.5 8.5 0 0 1-17 0Z" />
      <path d="M8.5 8.2c0-1.7 1.1-3.2 2.4-3.2 1.2 0 1.9 1.1 1.9 2.2 0 1.6-1.3 2.6-2.7 2.9" />
      <path d="M14.6 9.1c.7-1.3 2-2 2.9-1.5.8.5.7 1.7.1 2.5-.6.9-1.8 1.3-2.8 1.3" />
    </svg>
  )
}

export function Logo({ className, withName = true }: { className?: string; withName?: boolean }) {
  return (
    <Link to="/" className={cx('group inline-flex min-h-11 items-center gap-2.5', className)} aria-label={BRAND}>
      <span className="grid size-9 shrink-0 place-items-center rounded-[12px] bg-forest text-bg transition-transform duration-300 group-hover:-rotate-6">
        <BrandMark className="size-5" />
      </span>
      {withName && <span className="font-display whitespace-nowrap text-[clamp(12px,3.8vw,20px)] font-medium leading-none tracking-[-0.03em]">{BRAND}</span>}
    </Link>
  )
}

export function LanguageButtons({ className, onSelect }: { className?: string; onSelect?: () => void }) {
  const { lang, setLanguage } = useI18n()
  return (
    <div className={cx('flex items-center gap-1', className)} aria-label="Language">
      {languages.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => {
            setLanguage(code)
            onSelect?.()
          }}
          aria-pressed={lang === code}
          className={cx(
            'inline-flex min-h-11 items-center justify-center rounded-[11px] px-3 text-[13px] transition-colors',
            lang === code ? 'bg-forest text-bg' : 'text-muted hover:bg-surface-2 hover:text-fg',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

export function Nav() {
  const location = useLocation()
  const { t } = useI18n()
  const [menuOpen, setMenuOpen] = useState(false)
  const isHome = location.pathname === '/'

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-line bg-bg/95 backdrop-blur-xl">
      <Container className="flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {!isHome && (
            <Link to="/" className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg pr-1 text-[13px] text-muted transition-colors hover:text-forest">
              <ArrowLeft className="size-4" /> <span className="hidden sm:inline">{t('home')}</span>
            </Link>
          )}
          {!isHome && <span className="hidden h-4 w-px bg-line-strong sm:block" />}
          <Logo />
        </div>

        <nav className="hidden items-center gap-6 lg:flex" aria-label={t('site_sections')}>
          {links.map(({ to, key }) => (
            <Link key={key} to={to} className="inline-flex min-h-11 items-center text-[14px] text-muted transition-colors hover:text-forest">
              {t(key)}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:block">
          <LanguageButtons />
        </div>
        <button
          type="button"
          aria-label={menuOpen ? t('close') : t('menu')}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          className="grid size-11 shrink-0 place-items-center rounded-[12px] text-forest hover:bg-surface-2 lg:hidden"
        >
          {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </Container>

      {menuOpen && (
        <div className="absolute inset-x-0 top-full border-b border-line bg-bg px-4 pb-5 pt-2 shadow-soft sm:px-6 lg:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col" aria-label={t('menu')}>
            {links.map(({ to, key }) => (
              <Link key={key} to={to} onClick={() => setMenuOpen(false)} className="flex min-h-12 items-center border-b border-line text-[15px] text-fg">
                {t(key)}
              </Link>
            ))}
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted">{t('language')}</p>
            <LanguageButtons className="mt-1 flex-wrap justify-start" onSelect={() => setMenuOpen(false)} />
          </nav>
        </div>
      )}
    </header>
  )
}
